import {
  ActivityKeys,
  ADMIN_ROLES,
  AdminRoles,
  UserActivity,
  UserProfile,
} from "@ap-frontend/types"
import {
  confirmPasswordReset as firebaseConfirmPasswordReset,
  sendPasswordResetEmail as firebaseSendPasswordResetEmail,
  signInWithEmailAndPassword as firebaseSignIn,
  signOut as firebaseSignOut,
  updatePassword as firebaseUpdatePassword,
  User as FirebaseUser,
  verifyPasswordResetCode as firebaseVerifyPasswordResetCode,
  ParsedToken,
} from "firebase/auth"
import { FieldValue, SnapshotMetadata } from "firebase/firestore"
import React, { createContext, useContext, useEffect, useState } from "react"
import { useFirestoreDoc } from "../hooks/useFirestoreDoc"
import { FCWithChildren } from "../interfaces/core"
import {
  InvalidUserProfileError,
  validateUserProfile,
} from "../utility/userProfiles"
import { useFirebase } from "./MyCAppAppProvider"

interface Token extends ParsedToken {
  name: string
  applicationNumber: string
}

type DocumentStatus = "Fetched" | "Error" | "Pending" | "Inactive"

export interface UserAdmin {
  access: boolean
  roles: AdminRoles | undefined
  status: {
    isImpersonating: boolean
    documents: DocumentStatus
  }
  actions: {
    impersonateApplication: (newApplicationNumber: string) => void
    loadApplication: (newApplicationNumber: string) => void
    stopImpersonation: () => void
    setWaiveApplicationFee: (newApplicationFee: boolean) => Promise<void>
  }
}

export interface User {
  /**
   * The current logged in user
   *
   * It is:
   * - A User object if the user is logged in
   * - null if the user is not logged in
   * - undefined if the current status is unknown (eg. during rehydration)
   */
  user: FirebaseUser | null | undefined
  admin: UserAdmin
  applicationNumber: string | null | undefined
  profile: UserProfile | undefined
  activity: UserActivity | undefined
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  updateActivity: <ActivityKeyType extends ActivityKeys>(
    activityKey: ActivityKeyType,
    value: UserActivity[ActivityKeyType],
  ) => Promise<void>
  sendPasswordResetEmail: (email: string) => Promise<void>
  verifyResetPassword: (oobCode: string) => Promise<void>
  resetPassword: (oobCode: string, password: string) => Promise<void>
  updatePassword: (password: string) => Promise<void>
  metadata: {
    activity: SnapshotMetadata | undefined
  }
}

export const UserContext = createContext<User | undefined>(undefined)
UserContext.displayName = "UserContext"

/**
 * Gets the boolean claim from a JWT token.
 * @param token JWT token
 * @param claimName name of the claim
 * @returns claim value if it is boolean, otherwise returns false
 */
const getBooleanClaim = (token: Token, claimName: keyof AdminRoles) => {
  const claim = token[claimName]
  return (typeof claim === "boolean" && claim) || false
}

export const UserProvider: FCWithChildren = ({ children }) => {
  const [user, setUser] = useState<FirebaseUser | null | undefined>(undefined)
  const [applicationNumber, setApplicationNumber] = useState<
    string | null | undefined
  >(undefined)
  const [isAdmin, setIsAdmin] = useState<boolean>(false)
  const [isImpersonating, setIsImpersonating] = useState<boolean>(false)
  const [adminRoles, setAdminRoles] = useState<AdminRoles | undefined>()
  const [activity, setActivity] = useState<UserActivity | undefined>()
  const [activityMetadata, setActivityMetadata] = useState<
    SnapshotMetadata | undefined
  >(undefined)
  const [profile, setProfile] = useState<UserProfile | undefined>()

  const [documentStatus, setDocumentStatus] =
    useState<DocumentStatus>("Inactive")

  const profileDoc = useFirestoreDoc<UserProfile>(
    applicationNumber && `applicants/${applicationNumber}/userReadonly/profile`,
  )

  const activityDoc = useFirestoreDoc<UserActivity>(
    applicationNumber && `applicants/${applicationNumber}/user/activity`,
  )

  useEffect(() => {
    if (!applicationNumber) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setDocumentStatus("Inactive")

      setProfile(undefined)

      setActivity(undefined)
      setActivityMetadata(undefined)

      return
    }

    if (
      profileDoc.data &&
      profileDoc.data?.applicationNumber === applicationNumber &&
      activityDoc.data
    ) {
      let userProfileError: InvalidUserProfileError | undefined = undefined

      try {
        validateUserProfile(profileDoc.data)
        setProfile(profileDoc.data)
      } catch (e) {
        setProfile(undefined)
        if (e instanceof InvalidUserProfileError) {
          userProfileError = e
        } else {
          throw e // not expected
        }
      }

      setActivity(activityDoc.data)
      setActivityMetadata(activityDoc.metadata)

      if (userProfileError) {
        // FIXME: this doesn't have any effect because throwing the error unmounts this component
        setDocumentStatus("Error")
        throw new InvalidUserProfileError(
          `UserProfile doc from Firestore is invalid: ${userProfileError.message}`,
          { cause: userProfileError },
        )
      }
      setDocumentStatus("Fetched")
      return
    }

    // Don't keep previous documents metadata
    setActivityMetadata(undefined)

    if (profileDoc.error) {
      console.error(profileDoc.error)
      // FIXME: this doesn't have any effect because throwing the error unmounts this component
      setDocumentStatus("Error")
      throw new Error("Error loading profile document", {
        cause: profileDoc.error,
      })
    }

    if (activityDoc.error) {
      console.error(activityDoc.error)
      // FIXME: this doesn't have any effect because throwing the error unmounts this component
      setDocumentStatus("Error")
      throw new Error("Error loading activity document", {
        cause: activityDoc.error,
      })
    }

    setDocumentStatus("Pending")
  }, [applicationNumber, activityDoc, profileDoc])

  const { firebase, auth } = useFirebase()

  const setAuthProps = async (user: FirebaseUser | null | undefined) => {
    if (user) {
      try {
        const jwt = await user.getIdToken()
        const token: Token = JSON.parse(atob(jwt.split(".")[1]))
        const { applicationNumber, admin } = token
        if (applicationNumber) {
          setApplicationNumber(applicationNumber)
          setIsAdmin(false)
          setAdminRoles(undefined)
        } else if (typeof admin === "boolean" && admin) {
          setIsAdmin(admin)
          setAdminRoles(
            Object.fromEntries(
              ADMIN_ROLES.map(role => [role, getBooleanClaim(token, role)]),
            ) as AdminRoles,
          )
        }
      } catch (e) {
        console.warn("Error fetching user token", e)
      }

      setUser(user)
    } else {
      setUser(null)
      setApplicationNumber(null)
    }
  }

  const updateActivity = async <ActivityKeyType extends ActivityKeys>(
    activityKey: ActivityKeyType,
    value: UserActivity[ActivityKeyType],
  ) => {
    if (!user) throw new Error("app/not-authenticated")

    if (typeof activity === "undefined")
      throw new Error("app/activity-not-fetched")

    if (
      value instanceof FieldValue &&
      activity[activityKey] instanceof FieldValue &&
      value.isEqual(activity[activityKey] as FieldValue)
    ) {
      return
    } else if (
      !(value instanceof FieldValue) &&
      activity[activityKey] === value
    ) {
      return
    }

    return activityDoc.mutate(
      {
        ...activity,
        [activityKey]: value,
      },
      { merge: false },
    )
  }

  const sendPasswordResetEmail = async (email: string) => {
    if (!auth) throw new Error("app/auth-not-ready")

    await firebaseSendPasswordResetEmail(auth, email)
  }

  const verifyResetPassword = async (oobCode: string) => {
    if (!auth) throw new Error("app/auth-not-ready")

    await firebaseVerifyPasswordResetCode(auth, oobCode)
  }

  const resetPassword = async (oobCode: string, password: string) => {
    if (!auth) throw new Error("app/auth-not-ready")

    await firebaseConfirmPasswordReset(auth, oobCode, password)
  }

  const updatePassword = async (password: string) => {
    if (!user) throw new Error("app/auth-not-ready")

    await firebaseUpdatePassword(user, password)
  }

  const signIn = async (email: string, password: string) => {
    const userCreds = auth && (await firebaseSignIn(auth, email, password))
    setAuthProps(userCreds?.user)
  }

  const signOut = async () => {
    if (auth) await firebaseSignOut(auth)
    setAuthProps(null)
    setApplicationNumber(undefined)
    setIsAdmin(false)
    setAdminRoles(undefined)
  }

  const loadApplication = (newApplicationNumber: string) => {
    if (!adminRoles?.impersonation && !adminRoles?.audit) {
      throw new Error("user-provider/user-data/access-denied")
    }

    setApplicationNumber(newApplicationNumber)
  }

  const impersonateApplication = (newApplicationNumber: string) => {
    if (!adminRoles?.impersonation) {
      throw new Error("user-provider/impersonation/access-denied")
    }

    if (newApplicationNumber !== applicationNumber)
      loadApplication(newApplicationNumber)

    setIsImpersonating(true)
  }

  const stopImpersonation = () => {
    if (!adminRoles?.impersonation) {
      throw new Error("user-provider/impersonation/access-denied")
    }

    setIsImpersonating(false)
  }

  const setWaiveApplicationFee = async (value: boolean) => {
    if (!adminRoles?.manageFeeWaivers) {
      throw new Error("user-provider/waive-application-fee/access-denied")
    }

    await profileDoc.mutate({ waiveApplicationFee: value }, { merge: true })
  }

  useEffect(() => {
    const unsubscribe =
      auth && auth.onAuthStateChanged(user => setAuthProps(user))
    return unsubscribe
  }, [firebase, auth])

  const value = {
    user,
    admin: {
      access: isAdmin,
      roles: adminRoles,
      status: {
        isImpersonating,
        documents: documentStatus,
      },
      actions: {
        impersonateApplication,
        stopImpersonation,
        loadApplication,
        setWaiveApplicationFee,
      },
    },
    applicationNumber,
    profile,
    activity,
    signIn,
    signOut,
    updateActivity,
    sendPasswordResetEmail,
    resetPassword,
    verifyResetPassword,
    updatePassword,
    metadata: {
      activity: activityMetadata,
    },
  }

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>
}

export const useUser: () => User = () => {
  const userCtx = useContext(UserContext)

  if (userCtx === undefined) {
    throw Error("useUser hook must be used inside an UserProvider")
  }

  return userCtx
}
